import { reactive } from 'vue';

/**
 * Form handling composable that provides form validation and submission functionality
 * This replaces the form utility from utils/form.js
 *
 * @param {Object} initialValues - The initial form values
 * @param {Object} validationSchema - Yup validation schema
 * @returns {Object} Form object with values, errors, and helper methods
 */
export const useForm = (initialValues = {}, validationSchema = null) => {
  // Create reactive state
  const state = reactive({
    // Form values
    ...initialValues,

    // Form state
    errors: {},
    processing: false,
    successful: false,

    // Reset the form to its initial state
    reset() {
      Object.keys(initialValues).forEach((key) => {
        state[key] = initialValues[key];
      });
      state.errors = {};
      state.processing = false;
      state.successful = false;
    },

    // Set a single form value
    setData(key, value) {
      state[key] = value;
    },

    // Get all form values as an object
    data() {
      const formData = {};
      Object.keys(initialValues).forEach((key) => {
        formData[key] = state[key];
      });
      return formData;
    },

    // Validate the form using the provided validation schema
    async validate() {
      if (!validationSchema) return true;

      try {
        state.errors = {};
        await validationSchema.validate(state.data(), { abortEarly: false });
        return true;
      } catch (error) {
        if (error.inner) {
          error.inner.forEach((err) => {
            state.errors[err.path] = err.message;
          });
        } else {
          state.errors.message = error.message;
        }
        return false;
      }
    },

    // Submit the form data to an API
    async submit(promise) {
      state.processing = true;
      state.successful = false;
      state.errors = {};

      try {
        const response = await promise;
        state.processing = false;
        state.successful = true;
        return response;
      } catch (error) {
        state.processing = false;

        if (error.response && error.response.data) {
          // Handle API error responses
          const { data } = error.response;

          if (data.errors) {
            // Map API validation errors to form fields
            Object.keys(data.errors).forEach((key) => {
              state.errors[key] = data.errors[key][0];
            });
          } else if (data.message) {
            state.errors.message = data.message;
          }
        } else {
          // Handle general errors
          state.errors.message = error.message || 'An error occurred';
        }

        throw error;
      }
    },
  });

  return state;
};
